import {
  type CreditCard,
  useCreditCards,
} from "hooks/firestore/user/useCreditCards";
import { AnimatePresence, motion } from "motion/react";
import React, { useMemo } from "react";
import { MethodRowSkeleton } from "../../method-row/MethodRow";
import * as styles from "./SelectDebitCardStep.module.scss";
import {
  Badge,
  Button,
  DESKTOP_MEDIA_QUERY,
  Skeleton,
  SwipeToDelete,
  toast,
  useConfirmDialog,
  useMediaQuery,
  useWizard,
} from "@picklebet/ui";
import { useDepositWizard } from "features/wallet/components/deposit/hooks/useDepositWizard";
import { OptionRow } from "../../option-row/OptionRow";
import { CardIcon } from "../../card-icon/CardIcon";
import { cx } from "class-variance-authority";
import { useSelectedCard } from "features/wallet/components/deposit/hooks/useSelectedCard";
import { OptionRowAdd } from "../../option-row/OptionAddRow";
import { walletService } from "features/wallet/services/wallet";
import type { Maybe } from "types/utilities";
import type { DepositMethodStepName } from "features/wallet/components/deposit/utils/registry";
import { RemoveAction } from "../../method-row/RemoveAction";

const CardOptionsSkeleton = () => {
  return (
    <div className={styles.container}>
      <MethodRowSkeleton />

      <MethodRowSkeleton />

      <Skeleton className={styles.buttonSkeleton} />
    </div>
  );
};

const CardRow = ({
  card,
  selectedCard,
  lastUsedCard,
  onSelect,
  onRemove,
}: {
  card: CreditCard;
  selectedCard: Maybe<CreditCard>;
  lastUsedCard: Maybe<CreditCard>;
  onSelect: (card: CreditCard) => void;
  onRemove?: (cardId: string) => void;
}) => {
  const isExpired = card.expired;
  const isLastUsed = lastUsedCard?.id === card.id;

  return (
    <OptionRow
      label={card.name}
      sublabel={
        isExpired ? (
          <Badge className={cx(styles.badge, styles.expired)}>Expired</Badge>
        ) : isLastUsed ? (
          <Badge className={cx(styles.badge, styles.lastUsed)}>Last used</Badge>
        ) : undefined
      }
      icon={<CardIcon card={card} />}
      selected={card.id === selectedCard?.id}
      onSelect={() => onSelect(card)}
      disabled={isExpired}
      trailing={
        typeof onRemove === "function" && (
          <RemoveAction onClick={() => onRemove(card.id)} />
        )
      }
    />
  );
};

export const SelectDebitCardStep = () => {
  const isDesktop = useMediaQuery(DESKTOP_MEDIA_QUERY);
  const { goTo, back, reset } = useWizard<DepositMethodStepName>();
  const { setSelectedMethodId, setSelectedCardId } = useDepositWizard();
  const { selectedCard } = useSelectedCard();

  const { creditCards, isLoading } = useCreditCards();

  const { confirm, confirmDialog } = useConfirmDialog();

  const lastUsedCard = useMemo(() => {
    return creditCards.find(
      (card) => (card.successfulDepositCount || 0) > 0 && !card.expired,
    );
  }, [creditCards]);

  if (isLoading) {
    return <CardOptionsSkeleton />;
  }

  const handleRemoveCard = async (cardId: string) => {
    const confirmed = await confirm({
      title: "Remove Card",
      description: "Are you sure you want to remove this card?",
    });

    if (!confirmed) {
      return false;
    }

    const result = await walletService.removeCard(cardId);

    if (!result.ok) {
      toast({
        variant: "danger",
        title: "Failed to remove card",
        description:
          result.error.message ||
          "Something went wrong removing your card. Please try again.",
      });
      return false;
    }

    return true;
  };

  const onCardSelect = (card: CreditCard) => {
    setSelectedMethodId("debit-card");
    setSelectedCardId(card.id);
  };

  return (
    <>
      <div className={styles.container}>
        <div
          className={styles.container}
          role="radiogroup"
          aria-label="Select Card"
        >
          <AnimatePresence initial={false}>
            {creditCards.map((card) => {
              if (isDesktop) {
                return (
                  <motion.div
                    key={card.id}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2, ease: "easeIn" }}
                  >
                    <CardRow
                      key={card.id}
                      card={card}
                      lastUsedCard={lastUsedCard}
                      selectedCard={selectedCard}
                      onSelect={onCardSelect}
                      onRemove={handleRemoveCard}
                    />
                  </motion.div>
                );
              }

              return (
                <SwipeToDelete
                  key={card.id}
                  radius="var(--radius-10)"
                  onDelete={() => handleRemoveCard(card.id)}
                >
                  <CardRow
                    key={card.id}
                    card={card}
                    lastUsedCard={lastUsedCard}
                    selectedCard={selectedCard}
                    onSelect={onCardSelect}
                  />
                </SwipeToDelete>
              );
            })}
          </AnimatePresence>
        </div>

        <OptionRowAdd
          label="Add New Card"
          onClick={() => goTo("debit-card/add")}
        />

        <Button
          onClick={() => {
            if (selectedCard) {
              reset();
            } else {
              back();
            }
          }}
        >
          Done
        </Button>
      </div>

      {confirmDialog}
    </>
  );
};
